import './AlarmValue.scss';
import { useNavigate } from 'react-router-dom';
import { addDays, endOfDay, startOfDay, sub } from 'date-fns';
import Tooltip from '@mui/material/Tooltip';
import Icon from '../IconSvg/Icon';
import {
  getFarmTimeZoneFormat,
  redirectToFilteredAlarmPage,
  redirectToVDEDataExplorerPageForVectorEvent,
} from '../../domains';
import { useAppSelector } from '../../../reducers/rootReducer';
import { entityInfoSelectors } from '../../../reducers/entityInfoSlice';

interface AlarmValueProps {
  alarmValue: string | number;
  iconType: string;
  cssClassname?: string;
  externalId: string;
  is24h: boolean;
  isVector: boolean;
}

const VDE_APPENDIX_BY_VECTOR_EVENT_TYPE = {
  'lely-icon-pdb':
    '&offEvents=CriticalAlarm%2CNonCriticalAlarm%2CFrequencyTrip%2COperationMode%2CPowerLoss%2CStartup%2CUserLogin%2CCommunicationError%2CConverterError%2CExtraCharging%2CFeedDoorCloseRetry%2CMixerBlocked%2CRollerBlocked%2CAutoStart%2CBeltSlack%2CFgKitchenRefill%2CXPositionLost',
  'lely-icon-mfr':
    '&offEvents=CriticalAlarm%2CNonCriticalAlarm%2CFrequencyTrip%2COperationMode%2CPowerLoss%2CStartup%2CUserLogin%2CAutoStart%2CBeltSlack%2CFgKitchenRefill%2CXPositionLost%2CLedStateChange%2CIoPlayButtonPressed%2CIoSafetyCircuit',
  'lely-icon-grabber':
    '&offEvents=CriticalAlarm%2CNonCriticalAlarm%2CFrequencyTrip%2COperationMode%2CPowerLoss%2CStartup%2CUserLogin%2CCommunicationError%2CConverterError%2CExtraCharging%2CFeedDoorCloseRetry%2CMixerBlocked%2CRollerBlocked%2CLedStateChange%2CIoPlayButtonPressed%2CIoSafetyCircuit',
  criticalalarm:
    '&offEvents=NonCriticalAlarm%2CFrequencyTrip%2COperationMode%2CPowerLoss%2CStartup%2CUserLogin%2CCommunicationError%2CConverterError%2CExtraCharging%2CFeedDoorCloseRetry%2CMixerBlocked%2CRollerBlocked%2CAutoStart%2CBeltSlack%2CFgKitchenRefill%2CXPositionLost%2CLedStateChange%2CIoPlayButtonPressed%2CIoSafetyCircuit',
  noncriticalalarm:
    '&offEvents=CriticalAlarm%2CFrequencyTrip%2COperationMode%2CPowerLoss%2CStartup%2CUserLogin%2CCommunicationError%2CConverterError%2CExtraCharging%2CFeedDoorCloseRetry%2CMixerBlocked%2CRollerBlocked%2CAutoStart%2CBeltSlack%2CFgKitchenRefill%2CXPositionLost%2CLedStateChange%2CIoPlayButtonPressed%2CIoSafetyCircuit',
};

function AlarmValue({
  alarmValue,
  iconType,
  cssClassname,
  externalId,
  is24h,
  isVector,
}: AlarmValueProps) {
  const navigate = useNavigate();
  const entityInfo = useAppSelector(entityInfoSelectors.selectEntityInfo);
  const currentFarmTime = getFarmTimeZoneFormat(new Date(), entityInfo);
  const sevenDaysBefore = sub(currentFarmTime, { days: 7 });
  const oneDayBefore = sub(currentFarmTime, { days: 1 });
  const sevenDaysAfter = addDays(startOfDay(oneDayBefore), 7);

  const elementCssClassname = cssClassname
    ? `${cssClassname} lastWeekAlarmsNumber fs--b-xl`
    : 'lastWeekAlarmsNumber fs--b-xl';
  return (
    <span
      className={elementCssClassname}
      onClick={() => {
        if (isVector) {
          redirectToVDEDataExplorerPageForVectorEvent(
            navigate,
            externalId,
            is24h ? new Date(oneDayBefore) : new Date(sevenDaysBefore),
            is24h ? sevenDaysAfter : endOfDay(new Date().getTime()),
            VDE_APPENDIX_BY_VECTOR_EVENT_TYPE[cssClassname || iconType],
          );
        } else {
          redirectToFilteredAlarmPage(
            externalId,
            is24h ? new Date(oneDayBefore) : new Date(sevenDaysBefore),
            navigate,
            null,
            cssClassname?.toUpperCase(),
          );
        }
      }}
    >
      <Tooltip
        title={
          isVector
            ? `Click to navigate to Data Explorer`
            : `${cssClassname} | Click to navigate to Events page`
        }
        placement="top"
        arrow
        data-testid="alarm-value-tooltip"
      >
        <span className="icon">
          <Icon
            className="icon fs--h5"
            name={iconType.toLowerCase()}
            style={{ width: 20, height: 20 }}
          />
        </span>
      </Tooltip>
      <small>{alarmValue}</small>
    </span>
  );
}

export default AlarmValue;
